import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Heart, Target, Shield, Sparkles, Video, Users } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50">
      {/* Hero Section */}
      <nav className="px-6 py-6 flex justify-between items-center max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <Heart className="w-8 h-8 text-rose-500 fill-rose-500" />
          <span className="text-2xl font-bold text-rose-900" style={{fontFamily: 'Cormorant Garamond, serif'}}>Intentional Connections</span>
        </div>
        <Button
          data-testid="nav-login-btn"
          onClick={() => navigate('/auth')}
          className="bg-rose-500 hover:bg-rose-600 text-white px-6 py-2 rounded-full"
        >
          Sign In
        </Button>
      </nav>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-32 text-center">
        <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-rose-900 mb-6 leading-tight" style={{fontFamily: 'Cormorant Garamond, serif'}}>
          Find Love<br />With Intention
        </h1>
        <p className="text-lg md:text-xl text-gray-700 mb-12 max-w-2xl mx-auto">
          Marriage-minded dating for serious relationships. Skip the games, embrace meaningful connections guided by values, not swipes.
        </p>
        <Button
          data-testid="hero-get-started-btn"
          onClick={() => navigate('/auth')}
          size="lg"
          className="bg-rose-500 hover:bg-rose-600 text-white px-10 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
        >
          Get Started Free
        </Button>
      </div>

      {/* Features */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-4xl md:text-5xl font-bold text-center text-rose-900 mb-16" style={{fontFamily: 'Cormorant Garamond, serif'}}>
          Why Choose Us?
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <FeatureCard
            icon={<Users className="w-12 h-12 text-rose-500" />}
            title="Personal Matchmaker & Concierge"
            description="Work one-on-one with a dedicated matchmaker who hand-curates introductions for you. Both of you must say yes before a private chat opens — no cold messages."
          />
          <FeatureCard
            icon={<Target className="w-12 h-12 text-rose-500" />}
            title="Intent-Focused Matching"
            description="AI compatibility scoring built on your values, faith, relationship goals and love languages — not looks or endless swiping."
          />
          <FeatureCard
            icon={<Video className="w-12 h-12 text-rose-500" />}
            title="In-App Video Dates"
            description="Meet safely with guided, scheduled video dates built right into the app — no need to share your personal number until you're ready."
          />
          <FeatureCard
            icon={<Shield className="w-12 h-12 text-rose-500" />}
            title="Verified & Protected"
            description="Photo verification, optional background checks, and fraud detection so you can connect with genuine, serious singles in confidence."
          />
          <FeatureCard
            icon={<Sparkles className="w-12 h-12 text-rose-500" />}
            title="AI Dating Coach"
            description="Personalized profile help, tailored conversation starters, and creative date ideas — plus a warm AI intro when a new match opens."
          />
          <FeatureCard
            icon={<Heart className="w-12 h-12 text-rose-500" />}
            title="Quality Over Quantity"
            description="A few thoughtfully curated matches a day, timely match alerts, and weekly reflections keep you mindful and aligned with your goals."
          />
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-rose-500 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Ready to Find Your Person?
          </h2>
          <p className="text-xl text-rose-50 mb-8">
            Join thousands of singles looking for real, lasting love.
          </p>
          <Button
            data-testid="cta-join-btn"
            onClick={() => navigate('/auth')}
            size="lg"
            className="bg-white text-rose-600 hover:bg-rose-50 px-10 py-6 text-lg rounded-full shadow-lg"
          >
            Join Now
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-rose-900 text-rose-100 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p>&copy; 2026 Intentional Connections. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-rose-100">
    <div className="mb-4">{icon}</div>
    <h3 className="text-xl font-semibold text-rose-900 mb-3" style={{fontFamily: 'Cormorant Garamond, serif'}}>{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

export default LandingPage;